import React from 'react';
import { Environment } from 'src/Environment';
import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  Catalogue,
  ISalesforceService,
} from 'src/features/common';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect';
import { requestSignedMerchantAgreement } from 'src/services/merchant-agreement-service';

type RetryUploadMerchantAgreementProps = {
  dispatch: React.Dispatch<ApplicationAction>;
  application: Application;
  salesforceService: ISalesforceService;
  catalogue: Catalogue;
  lightVetting?: boolean;
};

export function RetryUploadMerchantAgreement(props: RetryUploadMerchantAgreementProps) {
  const isStoreAgreementEnabled = React.useMemo(
    (): boolean => !Environment.current.disabledFeatures.storeAgreement,
    [],
  );

  const sendMerchantAgreementToSF = () => {
    props.salesforceService
      .saveApplication(
        getApplicationAPIFromApplication(props.application, props.catalogue),
        props.lightVetting,
      )
      .then(() =>
        props.dispatch({
          type: ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED,
          isMerchantAgreementUploaded: true,
        }),
      )
      .catch(() => {}); // ignore errors, we'll try again on each step
  };

  const getSignedMerchantAgreement = async (screenshot?: string) => {
    if (screenshot) {
      try {
        const { signedAgreementPDF } = await requestSignedMerchantAgreement(
          props.application,
          screenshot,
        );
        props.dispatch({
          type: ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT,
          signedMerchantAgreement: signedAgreementPDF ?? '',
        });
      } catch (error) {
        return; // ignore errors, we'll try again on each step
      }
    }
  };

  useDeepCompareEffect(() => {
    if (
      isStoreAgreementEnabled &&
      !props.application.isMerchantAgreementUploaded &&
      !!props.application.signedMerchantAgreement
    ) {
      sendMerchantAgreementToSF();
    }

    if (isStoreAgreementEnabled && !props.application.signedMerchantAgreement) {
      getSignedMerchantAgreement(props.application.merchantAgreementScreenshot);
    }
  }, [
    props.application.isMerchantAgreementUploaded,
    isStoreAgreementEnabled,
    props.application.signedMerchantAgreement,
  ]);

  return null;
}
